/* eslint-disable no-unused-expressions, no-magic-numbers, max-lines, curly */

import ResizeTracker from 'utils/ResizeTracker';
import { scrollLock, scrollUnlock } from 'ui/scrollLock';

const state = {
    open: false,
    modalurl: null,
    trigger: null,
    modal: {
        content: null,
        title: null
    },
    touched: 0,
    isLocal: false,
    type: 'side',
    isAjax: false,
    isTranslated: false,
    scrollY: 0,
    focusEvent: null,
    onBoarding: false,
    init: false,
    overflow: false
};

const options = {
    mobileSize: 768,
    threshold: 20,
    errorTitle: 'Fout!',
    errorText: '',
    timeout: 50
};

class Modal {

    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        this._options = { ...Modal.options, ...options };
        this._element = element;
        this._state = state;
        this._handleCloseModal = this._handleCloseModal.bind(this);
        this._handleDocumentClicked = this._handleDocumentClicked.bind(this);
        this._handleResize = this._handleResize.bind(this);
        this._handleAjaxModalTrigger = this._handleAjaxModalTrigger.bind(this);
        this._fetchContent = this._fetchContent.bind(this);
        this._setFocusEvent = this._setFocusEvent.bind(this);
        this._removeFocusEvent = this._removeFocusEvent.bind(this);
        this._init();
    }

    /**
     * Initiate the modal
     *
     * @private
     */
    _init() {
        this._cacheSelectors();
        this._addListeners();
        this._setResizeTracker();
        this._handleResize();
        this._handleSessionReload();
        this._checkSessionOnBoarding();
    }

    /**
     * Cache selectors
     *
     * @private
     */
    _cacheSelectors() {
        this._state.init = true;
        this._elements = {
            modalTriggers: [...document.querySelectorAll('[data-modal-trigger]')],
            modalAjaxTriggers: [...document.querySelectorAll('[data-ajax-link]')],
            closeButtons: [...this._element.querySelectorAll('[data-close-modal]')],
            modalContent: this._element.querySelector('.modal__content'),
            modalHeader: this._element.querySelector('.modal__header'),
            onBoarding: document.querySelector('[data-onboarding]'),
            tabbedElements: null,
            body: document.body,
            layout: document.querySelector('.layout__outer')
        };
    }

    /**
     * Set the modal type
     *
     * @private
     * @param {*} trigger
     */
    _setModalType(trigger) {
        const modalType = trigger.getAttribute('data-modal-type');
        const modalOverflow = trigger.hasAttribute('data-modal-overflow');
        this._state.modalType = modalType ? modalType : 'side';
        modalOverflow && this._elements.body.setAttribute('data-modal-overflow', modalOverflow);
        this._state.overflow = modalOverflow;
        this._elements.body.setAttribute('data-modal-type', this._state.modalType);
    }

    /**
     * Add listeners
     *
     * @private
     */
    _addListeners() {
        this._elements.modalTriggers.forEach(trigger => {
            trigger.addEventListener('click', event => this._handleModalTrigger(event), false);

            trigger.addEventListener('focus', event => this._setFocusEvent(event), false);

            trigger.addEventListener('blur', () => this._removeFocusEvent(), false);
        });

        document.addEventListener('keydown', event => {
            const shouldReturn = event.composed && event.code.includes('Space') && event.ctrlKey && event.altKey;
            this._state.focusEvent && shouldReturn && this._handleAjaxModalTrigger(this._state.focusEvent);
        });

        this._elements.modalAjaxTriggers && this._elements.modalAjaxTriggers.forEach(trigger => {
            trigger.addEventListener('click', event => this._handleAjaxModalTrigger(event), false);

            trigger.addEventListener('focus', event => this._setFocusEvent(event), false);

            trigger.addEventListener('blur', () => this._removeFocusEvent(), false);
        });

        this._elements.closeButtons.forEach(closeButton => {
            closeButton.addEventListener('click', this._handleCloseModal);
        });

        document.addEventListener('click', event => this._handleDocumentClicked(event));

        // Create touchstart handler
        !this._elements.onBoarding && this._element.addEventListener('touchmove', event => {
            this._state.open && this._handleTouchMove(event);
        }, { passive: true });
    }

    /**
     * Close the modal on touchmove from left to right
     *
     * @private
     * @param {event} event
     */
    _handleTouchMove(event) {
        this._state.touched = this._state.touched === 0 ? event.touches[0].clientX : this._state.touched;
        const currentX = event.touches[0].clientX;
        currentX > this._state.touched + this._options.threshold && this._handleCloseModal();
        this._state.touched = currentX;
    }

    /**
     * Set the event in state on trigger focus
     *
     * @private
     * @param {event} event
     */
    _setFocusEvent(event) {
        this._state.focusEvent = event;
    }

    /**
     * Remove the event in state
     *
     * @private
     */
    _removeFocusEvent() {
        this._state.focusEvent = null;
    }

    /**
     * Get the modal content
     *
     * @private
     * @param {event} event
     */
    _handleModalTrigger(event) {
        event.preventDefault();
        const trigger = event.target;
        this._setModalType(trigger);
        const parentContainerAttr = trigger.getAttribute('data-modal-container');
        const parentContainer = parentContainerAttr && trigger.closest(parentContainerAttr).querySelector('[data-modal-content]');
        this._state.modal.content = parentContainer || trigger.parentNode.querySelector('[data-modal-content]');
        this._state.modal.title = this._state.modal.content.getAttribute('data-modal-title');
        this._state.url = trigger.getAttribute('data-modal-url');
        this._state.trigger = trigger;
        this._buildModal();
    }

    /**
     * Get the modal content
     *
     * @private
     * @param {event} event
     */
    _handleAjaxModalTrigger(event) {
        event.preventDefault();
        const trigger = event.target;
        this._setModalType(trigger);
        trigger.setAttribute('disabled', 'disabled');
        this._state.trigger = trigger;
        this._state.modalurl = trigger.getAttribute('href');

        this._fetchContent();
    }


    /**
     * Close the modal when clicked outside
     *
     * @private
     * @param {event} event
     */
    _handleDocumentClicked(event) {
        if (this._elements.onBoarding && this._state.isMobile) return;
        !event.target.closest('.modal') && !this._elements.modalTriggers.includes(event.target) && this._state.open && this._handleCloseModal();
    }

    /**
     * Close the modal
     *
     * @private
     */
    _handleCloseModal() {
        scrollUnlock();
        this._state.open = false;
        this._elements.modalContent.innerHTML = '';
        const title = this._elements.modalHeader.querySelector('h2');
        title && title.remove();
        this._element.setAttribute('data-open', this._state.open);
        this._elements.body.setAttribute('data-modal-open', false);
        this._element.setAttribute('aria-hidden', true);
        this._state.trigger && this._state.trigger.removeAttribute('disabled');
        this._state.trigger && this._state.trigger.focus();
        this._state.trigger = null;
        this._element.removeAttribute('tabindex');
        this._elements.layout.removeAttribute('aria-hidden');

        const isOnboarding = this._elements.body.getAttribute('data-modal-type') === 'centered' && !this._state.overflow;
        const timeout = setTimeout(() => {
            this._elements.body.removeAttribute('data-modal-type');
            this._state.modalType = null;
            clearTimeout(timeout);
        }, isOnboarding ? 2000 : 100);
    }

    /**
     * Fetch the content
     *
     * @private
     */
    _fetchContent() {
        const headers = this._state.isLocal ? {
            headers: { 'Content-Type': 'text/html; charset=utf-8' }
        } : { headers: { 'Content-Type': 'text/html; charset=utf-8' }, method: 'POST' };

        fetch(this._state.modalurl, { headers })
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                this._state.modal.content = doc.querySelector('[data-modal-content]');
                this._state.modal.title = doc.querySelector('[data-modal-title]').innerHTML;
                this._element.setAttribute('data-ajax-type', this._state.modal.content.tagName.toLowerCase());
                this._buildModal();
            })
            .catch(() => {
                this._handleError();
            });
    }

    /**
     * Build the modal content
     *
     * @private
     */
    _buildModal() {
        this._elements.modalContent.innerHTML = '';

        scrollLock();
        const clone = this._state.modal.content.cloneNode(true);
        this._elements.modalContent.appendChild(clone);

        if (this._state.modal.title) {
            const title = document.createElement('h2');
            title.innerHTML = this._state.modal.title;
            this._elements.modalHeader.append(title);
        }

        const jsModules = [...clone.querySelectorAll('[data-initialized')];
        if (jsModules.length > 0) {
            jsModules.forEach(module => module.removeAttribute('data-initialized'));
            window.initializeModules();
        }

        this._elements.modalAjaxTriggers && this._state.modalurl && window.initializeModules();


        this._state.open = true;
        this._element.setAttribute('data-open', this._state.open);
        this._elements.body.setAttribute('data-modal-open', true);
        this._element.removeAttribute('aria-hidden');
        this._element.setAttribute('tabindex', 0);
        this._element.focus();
        this._handleCloseButtons();
        this._handleTabbedElements();

        // Add the modal node to the tracking Matomo
        window._paq && window._paq.push(['trackContentImpressionsWithinNode', this._element]);
        // Re-initialise the Matomo screen recording when modal is opened, so personal data in the modal will be masked
        window._paq && window._paq.push(['HeatmapSessionRecording::setNewPageView', false]);
    }

    /**
     * Handle tabbing within the modal
     *
     * @private
     */
    _handleTabbedElements() {
        this._elements.tabbedElements = this._element.querySelectorAll('a, button, input, textarea');
        this._elements.tabbedElements[0].addEventListener('keydown', event => this._handleFirstTab(event));
        this._elements.tabbedElements[this._elements.tabbedElements.length - 1].addEventListener('keydown', event => this._handleLastTab(event));
    }

    /**
     * Focus last tab on back tabbing first tab
     *
     * @private
     * @param {event} event
     */
    _handleFirstTab(event) {
        if (event.keyCode !== 9 || !event.shiftKey) return;
        event.preventDefault();
        this._elements.tabbedElements[this._elements.tabbedElements.length - 1].focus();
    }

    /**
     * Focus first tab on tabbing last tab
     *
     * @private
     * @param {event} event
     */
    _handleLastTab(event) {
        if (event.keyCode !== 9 || event.shiftKey) return;
        event.preventDefault();
        this._elements.tabbedElements[0].focus();
    }

    /**
     * Change the content and title on error
     *
     * @private
     */
    _handleError() {
        this._state.modal.title = this._options.errorTitle;
        this._state.modal.content = this._options.errorText ? this._options.errorText : this._state.modal.content;
        this._buildModal();
    }


    /**
     * Set ResizeTracker
     *
     * @private
     */
    _setResizeTracker() {
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: this._elements.body },
            this._handleResize
        );
    }

    /**
     * Add click listener for each close button in the modal
     *
     * @private
     */
    _handleCloseButtons() {
        this._elements.closeButtons = [...this._element.querySelectorAll('[data-close-modal]')];
        this._elements.closeButtons.forEach(closeButton => {
            closeButton.addEventListener('click', this._handleCloseModal);
        });
    }

    /**
     * Change values on resize
     *
     * @private
     */
    _handleResize() {
        this._state.isMobile = this._elements.body.clientWidth < this._options.mobileSize;
    }

    /**
     * Scroll into position on page load
     *
     * @private
     */
    _handleSessionReload() {
        if (typeof Storage !== 'undefined') {
            const scrollY = window.localStorage.getItem('scrollY');
            if (!scrollY) return;
            this._state.scrollY = JSON.parse(scrollY);
            const timeout = setTimeout(() => {
                document.documentElement.scrollTop = parseInt(this._state.scrollY, 10);
                window.localStorage.removeItem('scrollY');
                this._state.scrollY = 0;
                clearTimeout(timeout);
            }, this._options.timeout);
        }
    }

    _checkSessionOnBoarding() {
        if (!this._elements.onBoarding) return;


        const timeout = setTimeout(() => {
            this._createOnBoarding();
            clearTimeout(timeout);
        }, 1000);
    }

    _createOnBoarding() {
        this._elements.body.setAttribute('data-modal-type', 'centered');
        const carousel = this._elements.onBoarding.querySelector('.carousel');
        if (!carousel) return;
        carousel.removeAttribute('data-initialized');
        this._state.modal.content = carousel;

        this._state.modal.title = null;

        this._state.url = null;
        this._buildModal();
        this._elements.onBoarding.remove();

        window.initializeModules();
    }
}

export default Modal;

/* eslint-enable */
